declare const __SKIP_AUTH__: boolean;
declare const __MAP_TOKEN__: string | null;
declare const __MAP_NAME__: string | null;
declare const __MANUAL_AUTH__: boolean;
// Compile-time flag: true ONLY in load-test builds (LOADTEST=1). Default false,
// so the credential-injection hooks below are dead-code-eliminated from every
// real (Gamma/Prod) bundle — no auth-injection surface ships to production.
declare const __LOADTEST__: boolean;

const loadTestBuild = typeof __LOADTEST__ !== 'undefined' && __LOADTEST__;

// Load-test harness may inject credentials via an init script that runs BEFORE
// this module loads (and before React mounts), so seed from the window global
// here. This guarantees hasManualCredentials() is already true on the very
// first render, so App.tsx never shows the TokenPrompt for automated runs.
// Only active in LOADTEST builds — never in real prod.
const loadTestGlobal = globalThis as unknown as {
  __LOADTEST_CREDS__?: { token: string; name: string };
  __setLoadTestCredentials__?: (token: string, name: string) => void;
};
const injected = loadTestBuild ? loadTestGlobal.__LOADTEST_CREDS__ : undefined;
let manualToken: string | null = injected?.token ?? null;
let manualName: string | null = injected?.name ?? null;

const authService = {
  async getToken(): Promise<string | null> {
    const token = typeof __MAP_TOKEN__ !== 'undefined' && __MAP_TOKEN__ ? __MAP_TOKEN__ : null;
    if (token) return token;
    const skipAuth = typeof __SKIP_AUTH__ !== 'undefined' && __SKIP_AUTH__;
    if (skipAuth) return 'dev-token';
    if (this.isManualAuth()) return manualToken;
    return null;
  },

  async getName(): Promise<string | null> {
    if (typeof __MAP_NAME__ !== 'undefined' && __MAP_NAME__) return __MAP_NAME__;
    if (this.isManualAuth()) return manualName;
    return null;
  },

  isManualAuth(): boolean {
    // !!! TEMPORARY — DO NOT MERGE !!! When a token is baked into the bundle
    // (TEMP_NO_AUTH_AUTHZ surfaces it via __MAP_TOKEN__), there is nothing for
    // the user to enter — getToken() returns it directly — so suppress the
    // manual "Enter Credentials" prompt even though the build set MANUAL_AUTH=1.
    if (typeof __MAP_TOKEN__ !== 'undefined' && __MAP_TOKEN__) return false;
    return typeof __MANUAL_AUTH__ !== 'undefined' && __MANUAL_AUTH__;
  },

  hasManualCredentials(): boolean {
    return !!(manualToken && manualName);
  },

  setManualCredentials(token: string, name: string): void {
    manualToken = token;
    manualName = name;
  },
};

// Load/stress-test hook: lets an automation harness (Playwright) inject a
// distinct MAP token per browser context so a pool of test accounts can drive
// many concurrent authenticated sessions. Gated behind __LOADTEST__ so it is
// compiled out of real prod/Gamma bundles entirely — no auth-injection surface
// ships to production.
if (loadTestBuild) {
  loadTestGlobal.__setLoadTestCredentials__ = (token: string, name: string) => {
    authService.setManualCredentials(token, name);
  };
}

export default authService;
